import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Bell, Send } from "lucide-react";

const PRESETS = [
  "Please update the status on this lead.",
  "Client is waiting — kindly follow up today.",
  "Overdue — call the lead ASAP.",
];

/**
 * SuperAdmin-only dialog to send a red-alert "Ping" to the lead's assignee.
 * The message is optional; presets exist for quick sends.
 */
export default function PingDialog({ lead, open, onOpenChange, onSent, memberName = "" }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setMessage(""); }, [open]);

  const send = async () => {
    if (!lead?.id) return;
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/ping`, { message });
      toast.success("Ping sent — the member will see a red alert on their dashboard.");
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <span className="w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center shadow-sm shadow-rose-200">
              <Bell className="w-4 h-4 text-white" />
            </span>
            Send a Ping
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Nudge <span className="font-medium">{memberName || "the assignee"}</span> about
            <span className="font-medium"> {lead?.name}</span>. They'll see a red alert on their dashboard.
          </p>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Message (optional)</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Any specific reason for the ping? e.g. 'Client called again — follow up today.'"
              rows={4}
              maxLength={500}
              data-testid="ping-message-input"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setMessage(p)}
                  className="text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600"
                  data-testid={`ping-preset-${p.slice(0, 10)}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={send}
            disabled={busy}
            className="bg-rose-500 hover:bg-rose-600 text-white"
            data-testid="ping-send-btn"
          >
            <Send className="w-4 h-4 mr-1.5" />
            {busy ? "Sending…" : "Send Ping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
